import {
    setUserIsImpersonating,
    signOutFailure,
    signOutSuccess,
} from 'actions/auth'
import { revokeRefreshToken } from 'api/Cerebro/unscoped/auth'
import { CEREBRO_REFRESH_TOKEN } from 'const/localStorage'
import { stopPlanHat } from 'features/Monitoring/PlanHat'
import { clearSegmentTraits } from 'features/Monitoring/Segment/service'
import { getPath } from 'routing/pagePaths'
import { getLocation, navigate } from 'routing/RoutingService'
import { selectDomainValue as selectAuthDomainValue } from 'selectors/auth'

import { deleteCerebroAuthTokens } from '../authTokens'
import { unsetDebugUserContext } from '../thirdParty'

import { dispatch, getReduxState } from './reduxBridge'

// Best effort: the tab signs out whether or not Cerebro could be reached.
async function revokeStoredRefreshToken(): Promise<void> {
    const refreshToken = localStorage.getItem(CEREBRO_REFRESH_TOKEN)
    if (!refreshToken) {
        return
    }
    try {
        await revokeRefreshToken(refreshToken)
    } catch {
        // The token leaves this browser regardless; nothing else to do.
    }
}

async function endSession({
    endSharedSession,
}: {
    endSharedSession: boolean
}): Promise<void> {
    const isImpersonating = selectAuthDomainValue(
        getReduxState(),
        'userIsImpersonating'
    )

    try {
        if (isImpersonating) {
            dispatch(setUserIsImpersonating(false))
        }

        dispatch(signOutSuccess())

        if (endSharedSession) {
            await revokeStoredRefreshToken()
            deleteCerebroAuthTokens()
        }
        unsetDebugUserContext()
        stopPlanHat()
        clearSegmentTraits()

        const location = getLocation()
        // Skip the redirect if we are already on the auth page
        if (location?.pathname !== getPath('authPage')) {
            navigate(getPath('authPage'), {
                state: { from: location },
            })
        }
    } catch (err: any) {
        dispatch(signOutFailure({ error: err.message }))
    }
}

/**
 * Sign the user out of the application: revoke the session on the server,
 * clear the auth tokens (which signs every other tab out as well), tear down
 * third-party identity, and redirect to the auth page.
 */
export async function signOut(): Promise<void> {
    await endSession({ endSharedSession: true })
}

/**
 * Tear this tab down after another tab ended the shared session. The tokens
 * are already revoked and gone from storage, so neither is touched here.
 */
export async function signOutLocally(): Promise<void> {
    await endSession({ endSharedSession: false })
}
